import { useState } from 'react'
import { FaLightbulb } from 'react-icons/fa'
import { FiChevronRight, FiFilter, FiPlus } from 'react-icons/fi'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { AnalyticsFeature } from '@domain/features/analytics/analytics-feature'
import WebinarCard from '@presentation/components/ui/WebinarCard'
import LoadingSpinner from '@presentation/components/ui/LoadingSpinner'
import { useAuthUser } from '@presentation/hooks/useAuthUser'
import { useScrollToTopOnRouteEnter } from '@presentation/hooks/useScrollToTopOnRouteEnter'
import { useWebinarCategories } from '@presentation/hooks/useWebinarCategories'
import { useWebinars, type WebinarSegment } from '@presentation/hooks/useWebinars'
import SeoHead from '@presentation/seo/SeoHead'

export default function WebinarListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const isWebinarsListRoute = location.pathname === '/webinars'
    || location.pathname === '/webinars/latest'
    || location.pathname === '/webinars/upcoming'
  const screenPath = `${location.pathname}${location.search || ''}`
  useScrollToTopOnRouteEnter({ enabled: isWebinarsListRoute })
  const { categories } = useWebinarCategories(true)
  const [categoryId, setCategoryId] = useState<string | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [draftCategoryId, setDraftCategoryId] = useState<string | null>(null)
  const segment: WebinarSegment = location.pathname === '/webinars/upcoming'
    ? 'upcoming'
    : 'latest'
  const { user } = useAuthUser()
  const {
    webinars,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
  } = useWebinars(segment, categoryId)
  const showInitialLoading = loading && webinars.length === 0
  const showBlockingError = Boolean(error) && webinars.length === 0

  const handleSegmentChange = (nextSegment: WebinarSegment) => {
    if (nextSegment === segment) {
      return
    }
    AnalyticsFeature.trackWebinarSegmentChange(screenPath, nextSegment)
    navigate(nextSegment === 'upcoming' ? '/webinars/upcoming' : '/webinars/latest')
  }

  const openFilterPopup = () => {
    setDraftCategoryId(categoryId)
    setIsFilterOpen(true)
  }

  const closeFilterPopup = () => {
    setIsFilterOpen(false)
  }

  const applyFilterSelection = () => {
    if (draftCategoryId) {
      AnalyticsFeature.trackFilterApply(screenPath, 'webinars_filter', draftCategoryId)
    } else {
      AnalyticsFeature.trackFilterClear(screenPath, 'webinars_filter')
    }
    setCategoryId(draftCategoryId)
    setIsFilterOpen(false)
  }

  const removeFilters = () => {
    AnalyticsFeature.trackFilterClear(screenPath, 'webinars_filter')
    setDraftCategoryId(null)
    setCategoryId(null)
    setIsFilterOpen(false)
  }

  const navigateBackToHome = () => {
    AnalyticsFeature.trackNavClick(screenPath, '/')
    if (window.history.length > 1) {
      navigate(-1)
      return
    }

    navigate('/', { replace: true })
  }

  return (
    <div className="bg-background pb-8 pt-3 sm:pb-12 sm:pt-4">
      {isWebinarsListRoute ? (
        <SeoHead
          title={
            segment === 'upcoming'
              ? "Upcoming Parkinson's Webinars | Parkinson's Insights"
              : "Latest Parkinson's Webinars | Parkinson's Insights"
          }
          description={
            segment === 'upcoming'
              ? "Explore upcoming Parkinson's webinars and register for expert-led sessions."
              : "Watch and browse the latest Parkinson's webinars featuring practical, science-based insights."
          }
          canonicalPath={segment === 'upcoming' ? '/webinars/upcoming' : '/webinars/latest'}
        />
      ) : null}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={navigateBackToHome}
          className="inline-flex items-center gap-1 text-sm font-semibold text-primary no-underline hover:text-primary-light md:hidden"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </button>

        <div className="mt-5 ml-1 flex items-center justify-between gap-3 pl-1 sm:mt-6 sm:ml-2 sm:pl-2">
          <h1 className="text-3xl font-bold tracking-tight text-black sm:text-4xl">
            Webinars
          </h1>
          <div className="flex items-center gap-2">
            {user?.isAdmin ? (
              <Link
                to="/webinars/create"
                onClick={() => AnalyticsFeature.trackNavClick(screenPath, '/webinars/create')}
                aria-label="Create webinar"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-blue bg-surface text-blue transition-colors hover:bg-blue/10"
              >
                <FiPlus className="h-5 w-5" aria-hidden="true" />
              </Link>
            ) : null}
            {categories.length > 0 && (
              <button
                type="button"
                onClick={openFilterPopup}
                className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold shadow-sm transition-colors ${
                  categoryId
                    ? 'border-blue bg-blue text-white hover:bg-blue/85'
                    : 'border-blue text-blue hover:bg-blue/8'
                }`}
              >
                <FiFilter className="h-4 w-4" aria-hidden="true" />
                Filter
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <div className="inline-flex rounded-xl border border-border-light bg-surface p-1 shadow-sm">
            <button
              type="button"
              onClick={() => handleSegmentChange('latest')}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                segment === 'latest'
                  ? 'bg-blue text-white'
                  : 'text-text-secondary hover:bg-border-light'
              }`}
            >
              Latest
            </button>
            <button
              type="button"
              onClick={() => handleSegmentChange('upcoming')}
              className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
                segment === 'upcoming'
                  ? 'bg-blue text-white'
                  : 'text-text-secondary hover:bg-border-light'
              }`}
            >
              Upcoming
            </button>
          </div>
          <Link
            to="/webinars/suggestions"
            className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-blue px-4 py-3 text-sm font-semibold text-white no-underline shadow-sm transition-[transform,opacity,background-color] duration-200 hover:scale-[1.02] hover:bg-blue/90 hover:opacity-90 sm:text-base"
          >
            <FaLightbulb className="size-4" aria-hidden="true" />
            Suggest a Webinar
            <FiChevronRight className="size-4" aria-hidden="true" />
          </Link>
        </div>

        {showInitialLoading ? (
          <LoadingSpinner className="mt-8" />
        ) : showBlockingError ? (
          <p className="mt-8 text-center text-text-secondary">
            Unable to load webinars. Please try again later.
          </p>
        ) : webinars.length === 0 ? (
          <div className="mt-12 text-center">
            <p className="text-lg font-semibold text-text-secondary">
              {segment === 'upcoming'
                ? 'No upcoming webinars available.'
                : 'No completed webinars available.'}
            </p>
            {categoryId ? (
              <p className="mt-1 text-sm text-text-muted">
                No webinars in this category. Try selecting a different one.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            {error ? (
              <p className="mt-6 text-center text-sm font-semibold text-text-secondary">
                Unable to refresh webinars. Showing the latest available results.
              </p>
            ) : null}
            <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {webinars.map((webinar) => (
                <WebinarCard
                  key={webinar.uid}
                  webinar={webinar}
                  showVideoPreview={segment === 'latest'}
                />
              ))}
            </div>

            {segment === 'latest' && hasMore ? (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => {
                    AnalyticsFeature.trackLoadMore(screenPath, 'webinars_list', 'webinars')
                    void loadMore()
                  }}
                  disabled={loadingMore}
                  className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loadingMore ? 'Loading...' : 'Load more'}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      {isFilterOpen && (
        <div className="fixed inset-0 z-60 bg-black/55 px-4 py-8 sm:px-6">
          <button
            type="button"
            className="absolute inset-0"
            aria-label="Close filters"
            onClick={closeFilterPopup}
          />

          <section
            role="dialog"
            aria-modal="true"
            aria-label="Webinar filters"
            className="relative z-10 mx-auto flex w-full max-w-xl flex-col rounded-2xl bg-surface p-5 shadow-2xl sm:p-6"
          >
            <h3 className="text-2xl font-bold text-text-primary">Filter webinars</h3>
            <p className="mt-1 text-base text-text-secondary">
              Select one category and apply when ready.
            </p>

            <div className="mt-5 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setDraftCategoryId(null)}
                className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                  !draftCategoryId
                    ? 'bg-blue text-white'
                    : 'bg-surface text-black shadow-sm hover:bg-border-light'
                }`}
              >
                All
              </button>
              {categories.map((category) => (
                <button
                  key={category.uid}
                  type="button"
                  onClick={() => {
                    setDraftCategoryId((current) => (
                      current === category.uid ? null : category.uid
                    ))
                  }}
                  className={`rounded-full px-5 py-2.5 text-base font-semibold transition-colors ${
                    draftCategoryId === category.uid
                      ? 'bg-blue text-white'
                      : 'bg-surface text-black shadow-sm hover:bg-border-light'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <button
                type="button"
                onClick={applyFilterSelection}
                className="w-full rounded-lg bg-violet px-5 py-2.5 text-base font-semibold text-white transition-opacity hover:opacity-90"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={closeFilterPopup}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={removeFilters}
                className="w-full px-2 py-2 text-base font-semibold text-black transition-colors hover:text-primary"
              >
                Remove filters
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
